import { io, Socket } from 'socket.io-client'
import { ref } from 'vue'
import { logger } from '@/utils/logger'

function getSocketBaseUrl(): string {
  const apiBase = import.meta.env.VITE_API_BASE_URL
  if (!apiBase) {
    return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001'
  }

  if (apiBase.startsWith('http://') || apiBase.startsWith('https://')) {
    return new URL(apiBase).origin
  }

  return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5001'
}

export interface TicketCheckInSuccessEvent {
  notificationId?: string
  messageId?: string
  ticketId?: string
  title?: string
  message?: string
  type?: string
  actionUrl?: string
  timestamp?: string
}

class UserNotificationSocketService {
  private socket: Socket | null = null
  private token: string | null = null
  private isConnected = ref(false)
  private checkInHandlers = new Set<(payload: TicketCheckInSuccessEvent) => void>()

  private connect() {
    if (!this.token) return

    if (this.socket) {
      if (this.socket.connected || this.socket.connected === false) {
        return
      }

      this.socket.removeAllListeners()
      this.socket.disconnect()
      this.socket = null
    }

    try {
      this.socket = io(`${getSocketBaseUrl()}/ws`, {
        auth: {
          token: this.token,
        },
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      })

      this.setupListeners()
    } catch (error) {
      logger.error('Failed to connect to notification WebSocket:', error)
    }
  }

  private setupListeners() {
    if (!this.socket) return

    this.socket.on('connect', () => {
      this.isConnected.value = true
    })

    this.socket.on('disconnect', () => {
      this.isConnected.value = false
    })

    this.socket.on('connect_error', (error) => {
      this.isConnected.value = false
      logger.error('Notification WebSocket connection error:', error)
    })

    this.socket.on('ticket_checkin_success', (payload: TicketCheckInSuccessEvent) => {
      this.checkInHandlers.forEach((handler) => handler(payload || {}))
    })
  }

  setToken(token: string | null) {
    if (token === this.token) return

    this.token = token
    if (!this.token) {
      this.disconnect()
      return
    }

    this.disconnect()
    this.connect()
  }

  onTicketCheckInSuccess(handler: (payload: TicketCheckInSuccessEvent) => void) {
    this.checkInHandlers.add(handler)
    return () => {
      this.checkInHandlers.delete(handler)
    }
  }

  disconnect() {
    if (!this.socket) return

    this.socket.disconnect()
    this.socket = null
    this.isConnected.value = false
  }
}

export const userNotificationSocketService = new UserNotificationSocketService()
